import { useContext, useEffect, useState } from 'react'
import axios from 'axios'
import { Form, Input, Layout, AlertContext } from '../widgets'
import * as routes from '../routes'
import {
  allowedImageTypes,
  awsClient,
  validateImageFile,
  withRouter,
  useTitle,
} from '../shared'

import cloudImg from '../images/cloud.svg'

const UploadPreset = (props) => {
  const [state, replaceState] = useState({
    submitting: false,
    presetFileName: null,
    preset: null,
    prefilledName: null,
    image: null,
    imagePreview: null,
    name: '',
    description: '',
  })

  const setState = (subState) => {
    replaceState(state => {
      return {...state, ...subState}
    })
  }

  const { showAlertError, handleError } = useContext(AlertContext)

  useTitle('Upload Preset')

  // free the previous preview when it's replaced and when leaving the page
  useEffect(() => () => {
    if (state.imagePreview) {
      URL.revokeObjectURL(state.imagePreview)
    }
  }, [state.imagePreview])

  const handlePresetFileChanged = (event) => {
    const file = event.target.files[0]
    setState({ presetFileName: null, preset: null })
    if (!file) {
      return
    }
    // ignore the result if another file was chosen while this one was being read
    const isCurrentFile = () => event.target.files[0] === file
    file.text().then((text) => {
      if (!isCurrentFile()) { return }
      const preset = JSON.parse(text)
      if (typeof preset !== 'object' || Array.isArray(preset) || preset === null) {
        throw new Error('invalid preset')
      }
      const presetName = typeof preset.name === 'string' ? preset.name : ''
      replaceState(state => {
        // keep a name the user typed, but replace one prefilled from a previously chosen file
        const keepName = state.name && state.name !== state.prefilledName
        return {...state, presetFileName: file.name, preset, prefilledName: presetName, name: keepName ? state.name : presetName}
      })
    }).catch(() => {
      if (!isCurrentFile()) { return }
      event.target.value = ''
      setState({ presetFileName: null, preset: null })
      showAlertError('The chosen file is not a valid preset file.')
    })
  }

  const handleImageChanged = (event) => {
    const file = event.target.files[0]
    if (!file) {
      return
    }
    validateImageFile(file, (error) => {
      if (error) {
        event.target.value = ''
        setState({ image: null, imagePreview: null })
        showAlertError(error)
      } else {
        setState({ image: file, imagePreview: URL.createObjectURL(file) })
      }
    })
  }

  const uploadImage = (presetId) => {
    return axios.get(
      routes.dynamic(routes.getUrlPresetUrl, presetId)
    ).then((response) => {
      const { image_name, image_upload_url } = response.data
      return awsClient.put(image_upload_url, state.image, { headers: { 'Content-Type': 'binary/octet-stream' } }).then(() => {
        return axios.patch(routes.dynamic(routes.presetUrl, presetId), { image_name })
      })
    })
  }

  const handleUploadPreset = (e) => {
    e.preventDefault()
    setState({ submitting: true })
    axios.post(routes.postPresetUrl, {
      preset: state.preset,
      name: state.name,
      description: state.description || null,
      source: 'plugin',
    }).then((response) => {
      const presetId = response.data.id
      const showPresets = () => props.router.navigate(routes.producerPresetsPath)
      // the preset already exists, so leave this page even if the image fails to avoid a duplicate upload
      return (state.image ? uploadImage(presetId) : Promise.resolve()).then(showPresets, (error) => {
        showPresets()
        handleError(error)
      })
    }).catch((error) => {
      handleError(error)
      setState({ submitting: false })
    })
  }

  return (
    <div className='body-container'>
      <Layout>
        <Layout.Header>
          Upload Preset
        </Layout.Header>
        <div className='layout-content'>
          <Form layout='vertical' onSubmit={handleUploadPreset}>
            <div className='upload-preset__files'>
              <label className='upload-preset__picker upload-preset__file'>
                <img src={cloudImg} alt='' />
                <div>
                  <div className='upload-preset__title'>{state.presetFileName || 'Select Preset File to Upload'}</div>
                  <div className='upload-preset__description'>
                    In the Preset Editor, tap &quot;Save&quot; on your custom Preset and choose a destination. Select that file here.
                  </div>
                </div>
                <input type='file' hidden onChange={handlePresetFileChanged} />
              </label>
              <label className='upload-preset__picker upload-preset__image'>
                {state.imagePreview ?
                  <img className='upload-preset__image-preview' src={state.imagePreview} alt='' /> :
                  <>
                    <img src={cloudImg} alt='' />
                    <div className='upload-preset__title'>Select Cover Image</div>
                  </>
                }
                <input type='file' hidden accept={allowedImageTypes} onChange={handleImageChanged} />
              </label>
            </div>
            <Form.Item label='Preset Name'>
              <Input
                required
                type='text'
                value={state.name}
                onChange={(event) => setState({ name: event.target.value })}
              />
            </Form.Item>
            <Form.Item label='Description'>
              <Input.TextArea
                rows={6}
                value={state.description}
                onChange={(event) => setState({ description: event.target.value })}
              />
            </Form.Item>
            <div className='upload-preset__submit'>
              <Form.Button
                loading={state.submitting}
                disabled={!state.preset || !state.name.trim()}
              >
                Upload
              </Form.Button>
            </div>
          </Form>
        </div>
      </Layout>
    </div>
  )
}

export default withRouter(UploadPreset)
